/// <reference types="bootstrap" />
/// <reference types="grecaptcha" />
import {
	ChangeAdvanceIntervalEvent, ChangeFontSizeEvent, KeyboardShortcutsEvent,
	ReportErrorEvent, ResetTimerEvent, ShowReportModalEvent, ToggleAdvanceEvent,
	ToggleTimerEvent, EnterEmailEvent, InputOverrideEvent
} from "../../events";
import { FilterValue } from "../../lib/filter";
import { ItemId, Serializer } from "../../model/datasource";
import { ContentItem } from "../../model/datasource/item";
import { ListItem } from "../../model/datasource/list";
import { BindRecord } from "./frontend";

function serializeErrors(errors: (Error | any)[]): string {
	return JSON.stringify(errors.reduce(
		(serialized: (string | any)[], error: Error | any) => {
			if (error instanceof Object) {
				let message = `${error} (${error.stack})`;
				const props = Object.getOwnPropertyNames(error)
					.filter(k => !['message', 'stack'].includes(k));
				if (props && props.length) {
					message += ' ' + JSON.stringify(props.reduce(
						(props, prop) => ({
							...props, [prop]: error[prop]
						}), {}
					));
				}
				serialized.push(message);
			} else {
				serialized.push(error);
			}
			return serialized;
		}, []
	));
}

export class SettingsFrontend<I extends ItemId> {
	itemIdSerializer: Serializer<I>;
	constructor(item: Serializer<I>) {
		this.itemIdSerializer = item;
	}

	getBindToggleTimer(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('input#show-timer'),
			'change',
			element => new ToggleTimerEvent()
		);
	}

	getBindKeyboardShortcuts(): BindRecord {
		return new BindRecord<HTMLInputElement>(
			document.querySelectorAll('input#keyboard-shortcuts'),
			'change',
			element => new KeyboardShortcutsEvent(element.checked)
		);
	}

	getBindChangeFontSize(): BindRecord {
		return new BindRecord<HTMLInputElement>(
			document.querySelectorAll('input#fontsize'),
			'change',
			element => new ChangeFontSizeEvent((parseFloat(element.value) / 100) * 2 + 0.5)
		);
	}

	getBindResetFontSize(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.fontsize-reset'),
			'click',
			// TODO: Find a better default initialization
			element => new ChangeFontSizeEvent(1)
		);
	}

	getBindToggleReport(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('a.report-toggle'),
			'click',
			element => new ShowReportModalEvent()
		);
	}

	getBindEnterEmail(): BindRecord {
		return new BindRecord<HTMLInputElement>(
			document.querySelectorAll('input[name=email]'),
			'input',
			element => new EnterEmailEvent(element)
		)
	}

	getBindOverrideInput(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('a.input-override'),
			'click',
			element => new InputOverrideEvent(
				element.closest('form'),
				element.dataset.selector,
				element.dataset.value,
			),
			true
		);
	}

	getBindReportError(): BindRecord {
		return new BindRecord<HTMLFormElement>(
			document.querySelectorAll<HTMLFormElement>('form.report'),
			'submit',
			element => new ReportErrorEvent(element),
			true
		);
	}

	getBindToggleAutoAdvance(): BindRecord {
		return new BindRecord<HTMLInputElement>(
			document.querySelectorAll('.advance-toggle'),
			'click',
			element => new ToggleAdvanceEvent(element.value.toLowerCase() == 'true')
		);
	}

	getBindChangeAdvanceInterval(): BindRecord {
		return new BindRecord<HTMLInputElement>(
			document.querySelectorAll('input#advance-interval'),
			'change',
			element => new ChangeAdvanceIntervalEvent(parseInt(element.value) * 1000)
		);
	}

	getBindResetTimer(): BindRecord {
		return new BindRecord(
			document.querySelectorAll('.reset-timer'),
			'click',
			element => new ResetTimerEvent()
		);
	}

	displayFontSize(size: number): void {
		document.querySelector<HTMLInputElement>(
			'input#fontsize'
		).value = ((size - 0.5) / 2 * 100).toString();
	}

	displayKeyboardShortcuts(enabled: boolean): void {
		document.querySelector<HTMLInputElement>(
			'input#keyboard-shortcuts'
		).checked = enabled;
	}

	displayTimer(show: boolean): void {
		document.querySelector<HTMLInputElement>(
			'input#show-timer'
		).checked = show;
	}

	displayErrorLoading(show: boolean): void {
		const loading = document.querySelector('.report-loading');
		if (show) {
			loading.classList.remove('d-none');
		} else {
			loading.classList.add('d-none');
		}
	}

	displayErrorModal(jQuery: JQueryStatic, url: string, item: ContentItem<I> | ListItem<I>, filters: FilterValue[], screenshot: string, errors: (Error | any)[], localStorage: Record<string, any>): void {
		const form = document.querySelector<HTMLFormElement>('.report-error form.report');
		const image = document.querySelector<HTMLImageElement>('.report-error img.screenshot');
		form.url.value = url;
		form.item.value = JSON.stringify(item);
		form.filter.value = JSON.stringify(filters);
		form.screenshot.value = screenshot;
		form.errors.value = serializeErrors(errors);
		form.localstorage.value = JSON.stringify(localStorage);
		image.src = screenshot;
		grecaptcha.reset();
		document.querySelector('.report-error .error').innerHTML = '';
		jQuery('.report-error').modal('show');
	}

	hideErrorModal(jQuery: JQueryStatic): void {
		const form = document.querySelector<HTMLFormElement>('.report-error form.report');
		jQuery('.report-error').modal('hide');
		form.email.value = '';
		form.description.value = '';
	}

	displayInputSuggestion(element: HTMLElement, suggestion: string | undefined): void {
		const form = element.closest<HTMLFormElement>('form');
		const suggestionElement = form.querySelector<HTMLDivElement>(element.dataset.suggestion);
		const valueLink = suggestionElement.querySelector<HTMLLinkElement>('a.value');
		if (suggestion) {
			valueLink.innerText = valueLink.dataset.value = suggestion;
		}
		suggestionElement.classList.toggle('d-none', !suggestion);
	}

	updateInputs(root: HTMLElement, selector: string, value: string): HTMLInputElement[] {
		const targets = Array.from(root.querySelectorAll<HTMLInputElement>(selector));
		for (const t of targets) {
			t.value = value;
		}
		return targets;
	}

	displayErrorResultModal(jQuery: JQueryStatic, success: boolean, reason?: string): void {
		const title = document.querySelector('.report-status .title');
		const body = document.querySelector('.report-status .body');
		title.innerHTML = success ? 'Report Submitted' : 'Report Failed';
		body.innerHTML = success ?
			'Your report has been successfully submitted.':
			'Your report has not succesfully submitted: ' + reason;
		jQuery('.report-status').modal('show');
	}

	toggleShortcutModal(jQuery: JQueryStatic): void {
		jQuery('.keyboard-reference').modal('toggle');
	}

	displayReportErrorMessage(message: string): void {
		document.querySelector('.report-error .error').innerHTML = message;
	}

	displayAdvance(enable: boolean): void { }

	displayAdvanceInterval(interval: number): void {
		document.querySelector<HTMLInputElement>(
			'input#advance-interval'
		).value = `${interval / 1000}`;
	}

	displayTimed(enable: boolean): void {
		document.querySelectorAll('input.pause').forEach(
			(element: HTMLInputElement) => { element.checked = enable; }
		);
	}
}

